import { create } from 'zustand';
import { adminAuctionApi } from '@api/api-config.ts';
import {
  AdminAuctionDetailResponse,
  AdminAuctionSupplierResponse,
  AdminAuctionProductResponse,
  AdminAuctionPriceResponse,
  AdminAuctionCustomerResponse,
  AdminAuctionOrganizationLovResponse,
  PriceType,
} from '@api/generated/portal-be-admin';

interface PaginationState {
  page: number;
  size: number;
  total: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
}

interface AdminAuctionDetailStoreState {
  // Základní detail
  loading: boolean;
  error: string | null;
  auction: AdminAuctionDetailResponse | null;

  // Dodavatelé
  suppliers: AdminAuctionSupplierResponse[];
  suppliersLoading: boolean;
  suppliersError: string | null;
  suppliersPagination: PaginationState | null;

  // Produkty
  products: AdminAuctionProductResponse[];
  productsLoading: boolean;
  productsError: string | null;
  productsPagination: PaginationState | null;

  // Ceny
  prices: AdminAuctionPriceResponse[];
  pricesLoading: boolean;
  pricesError: string | null;
  pricesPagination: PaginationState | null;
  pricesExporting: boolean;

  // Zákazníci
  customers: AdminAuctionCustomerResponse[];
  customersLoading: boolean;
  customersError: string | null;
  customersPagination: PaginationState | null;

  // Nabídky do filtrů (celé seznamy, nezávislé na stránkování záložek)
  supplierLov: AdminAuctionOrganizationLovResponse[];
  customerLov: AdminAuctionOrganizationLovResponse[];

  // Akce
  loadAuctionDetail: (auctionId: number) => Promise<void>;
  loadSuppliers: (auctionId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadProducts: (auctionId: number, params: { page?: number; size?: number; q?: string; supplierId?: number }) => Promise<void>;
  loadPrices: (
    auctionId: number,
    params: { page?: number; size?: number; supplierId?: number; customerId?: number; productQuery?: string; priceType?: PriceType; guaranteed?: boolean },
  ) => Promise<void>;
  exportPrices: (
    auctionId: number,
    params: { supplierId?: number; customerId?: number; productQuery?: string; priceType?: PriceType; guaranteed?: boolean },
  ) => Promise<void>;
  loadCustomers: (auctionId: number, params: { page?: number; size?: number; q?: string }) => Promise<void>;
  loadFilterLov: (auctionId: number) => Promise<void>;
  reset: () => void;
}

export const useAdminAuctionDetailStore = create<AdminAuctionDetailStoreState>(set => ({
  // Základní detail
  loading: false,
  error: null,
  auction: null,

  // Dodavatelé
  suppliers: [],
  suppliersLoading: false,
  suppliersError: null,
  suppliersPagination: null,

  // Produkty
  products: [],
  productsLoading: false,
  productsError: null,
  productsPagination: null,

  // Ceny
  prices: [],
  pricesLoading: false,
  pricesError: null,
  pricesPagination: null,
  pricesExporting: false,

  // Zákazníci
  customers: [],
  customersLoading: false,
  customersError: null,
  customersPagination: null,

  // Nabídky do filtrů
  supplierLov: [],
  customerLov: [],

  loadAuctionDetail: async (auctionId: number) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAuctionApi.getAuctionDetail({ auctionId });
      set({
        loading: false,
        error: null,
        auction: response.data,
      });
    } catch (error) {
      set({
        loading: false,
        error: error instanceof Error ? error.message : 'Chyba při načítání detailu aukce',
        auction: null,
      });
    }
  },

  loadSuppliers: async (auctionId: number, params) => {
    try {
      set({ suppliersLoading: true, suppliersError: null });
      const response = await adminAuctionApi.getAuctionSuppliers({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        suppliersLoading: false,
        suppliersError: null,
        suppliers: response.data.items,
        suppliersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        suppliersLoading: false,
        suppliersError: error instanceof Error ? error.message : 'Chyba při načítání dodavatelů',
        suppliers: [],
        suppliersPagination: null,
      });
    }
  },

  loadProducts: async (auctionId: number, params) => {
    try {
      set({ productsLoading: true, productsError: null });
      const response = await adminAuctionApi.getAuctionProducts({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
        supplierId: params.supplierId,
      });
      set({
        productsLoading: false,
        productsError: null,
        products: response.data.items,
        productsPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        productsLoading: false,
        productsError: error instanceof Error ? error.message : 'Chyba při načítání produktů',
        products: [],
        productsPagination: null,
      });
    }
  },

  loadPrices: async (auctionId: number, params) => {
    try {
      set({ pricesLoading: true, pricesError: null });
      const response = await adminAuctionApi.getAuctionPrices({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        supplierId: params.supplierId,
        customerId: params.customerId,
        productQuery: params.productQuery,
        priceType: params.priceType,
        isGuaranteed: params.guaranteed,
      });
      set({
        pricesLoading: false,
        pricesError: null,
        prices: response.data.items,
        pricesPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        pricesLoading: false,
        pricesError: error instanceof Error ? error.message : 'Chyba při načítání cen',
        prices: [],
        pricesPagination: null,
      });
    }
  },

  // Export bere celý filtrovaný výsledek, ne jen zobrazenou stránku - stránkování se serveru neposílá.
  exportPrices: async (auctionId: number, params) => {
    set({ pricesExporting: true, pricesError: null });
    try {
      const response = await adminAuctionApi.exportAuctionPrices(
        {
          auctionId,
          supplierId: params.supplierId,
          customerId: params.customerId,
          productQuery: params.productQuery,
          priceType: params.priceType,
          isGuaranteed: params.guaranteed,
        },
        { responseType: 'blob' },
      );
      const url = window.URL.createObjectURL(response.data as unknown as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `aukce-${auctionId}-ceny.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      set({ pricesExporting: false });
    } catch (error) {
      set({
        pricesExporting: false,
        pricesError: error instanceof Error ? error.message : 'Export cen se nepodařilo stáhnout',
      });
    }
  },

  loadCustomers: async (auctionId: number, params) => {
    try {
      set({ customersLoading: true, customersError: null });
      const response = await adminAuctionApi.getAuctionCustomers({
        auctionId,
        page: params.page || 1,
        size: params.size || 20,
        q: params.q,
      });
      set({
        customersLoading: false,
        customersError: null,
        customers: response.data.items,
        customersPagination: {
          page: response.data.page,
          size: response.data.size,
          total: response.data.total,
          totalPages: response.data.totalPages,
          hasNext: response.data.hasNext,
          hasPrev: response.data.hasPrev,
        },
      });
    } catch (error) {
      set({
        customersLoading: false,
        customersError: error instanceof Error ? error.message : 'Chyba při načítání zákazníků',
        customers: [],
        customersPagination: null,
      });
    }
  },

  // Dodavatelé a zákazníci do filtrů - celé seznamy, aby v nabídce nechyběl nikdo mimo první stránku.
  loadFilterLov: async (auctionId: number) => {
    try {
      const [suppliersResponse, customersResponse] = await Promise.all([
        adminAuctionApi.getAuctionSupplierLov({ auctionId }),
        adminAuctionApi.getAuctionCustomerLov({ auctionId }),
      ]);
      set({ supplierLov: suppliersResponse.data, customerLov: customersResponse.data });
    } catch {
      // Selhání nabídky filtru nesmí shodit detail aukce - zůstane prázdná (= "Všichni").
      set({ supplierLov: [], customerLov: [] });
    }
  },

  reset: () => {
    set({
      loading: false,
      error: null,
      auction: null,
      suppliers: [],
      suppliersLoading: false,
      suppliersError: null,
      suppliersPagination: null,
      products: [],
      productsLoading: false,
      productsError: null,
      productsPagination: null,
      prices: [],
      pricesLoading: false,
      pricesError: null,
      pricesPagination: null,
      pricesExporting: false,
      customers: [],
      customersLoading: false,
      customersError: null,
      customersPagination: null,
      supplierLov: [],
      customerLov: [],
    });
  },
}));
